// Procedural WebAudio for UGH!3D — no sound files, ever. Everything here is
// oscillators, filters and ONE shared white-noise buffer.
//
//   destination <- limiter <- masterGain <- [sfxBus, ambBus, uiBus]
//                                 ^-- feedback-delay hall reverb (parallel send)
//
// Must be unlocked by a user gesture (initAudio on click/keydown).
//
// Continuous-node budget vs. the old mix: -11 (rotor buffer-AM chain, pedal
// creak FM pair, rain chain) / +17 rotor drivetrain (blade pulse, tip whoosh,
// shaft, sputter LFO) +6 rain +20 reverb +7 hall wind +15 insects +9 river.
// The rotor itself is a wash (-3 creak nodes, +2 pulse nodes); the growth is
// the hall (reverb + ambience), all of it filters/oscillators, no convolution.
import { mulberry32 } from './rng.js';
import { CAVES } from '../shared/level.mjs';

let ctx = null, limiter = null, masterGain = null;
let sfxBus = null, ambBus = null, uiBus = null;
let NOISE = null, RASP = null;
let revIn = null, revLP = null, revGain = null;

// Rotor visual constants — these MIRROR applyHeliVisual() in heli.js. Kept here
// so the blade pulse can sit exactly on the rotor the player sees spinning.
const ROTOR_W = 22, BLADES = 4, CRANK_W = 12;
const BLADE_HZ_MAX = ROTOR_W / (2 * Math.PI) * BLADES;   // 14.006 blade passes/s

const BUS_BASE = { sfx: 0.55, amb: 0.50, ui: 0.50 };
const clamp = (v, a, b) => v < a ? a : v > b ? b : v;
const clamp01 = v => clamp(v, 0, 1);

let userVol = 1, muted = false;
let sendScale = 1;                       // set by setRoom(): the hall's wetness

// ------------------------------------------------------------------ init
export function initAudio() {
  if (ctx) { ctx.resume(); return; }
  ctx = new (window.AudioContext || window.webkitAudioContext)();

  // brickwall limiter: only ever touches rare peaks, so a roar no longer
  // squashes the rotor, the rain and the coins for a second and a half
  limiter = ctx.createDynamicsCompressor();
  limiter.threshold.value = -6; limiter.ratio.value = 20; limiter.knee.value = 0;
  limiter.attack.value = 0.002; limiter.release.value = 0.12;
  limiter.connect(ctx.destination);

  try { muted = localStorage.getItem('ugh3d.muted') === '1'; } catch (e) { muted = false; }
  masterGain = ctx.createGain(); masterGain.gain.value = muted ? 0 : userVol;
  masterGain.connect(limiter);

  sfxBus = ctx.createGain(); sfxBus.gain.value = BUS_BASE.sfx; sfxBus.connect(masterGain);
  ambBus = ctx.createGain(); ambBus.gain.value = BUS_BASE.amb; ambBus.connect(masterGain);
  uiBus = ctx.createGain(); uiBus.gain.value = BUS_BASE.ui; uiBus.connect(masterGain);

  // one 4-second noise buffer for the whole game — allocated once while the
  // title screen is up, then shared by every burst, loop and hiss below
  const nl = Math.floor(ctx.sampleRate * 4);
  NOISE = ctx.createBuffer(1, nl, ctx.sampleRate);
  const nd = NOISE.getChannelData(0);
  for (let i = 0; i < nl; i++) nd[i] = Math.random() * 2 - 1;

  // shared throat-rasp curve for the voice engine (built once, never per grunt)
  RASP = new Float32Array(1024);
  for (let i = 0; i < 1024; i++) RASP[i] = Math.tanh(2.6 * (i / 511.5 - 1));

  buildReverb();
  buildRotor();
  buildRain();
  startAmbience();

  // WebAudio is not throttled like rAF, so a hidden tab keeps the jungle
  // playing at full gain. The photo/video pipeline runs on virtual time and
  // must never be suspended under it.
  document.addEventListener('visibilitychange', () => {
    if (window.__photoCam) return;
    if (document.hidden) ctx.suspend(); else ctx.resume();
  });

  schedTimer = setInterval(schedTick, 250);
}

// Kept as !!ctx on purpose: setRotor/setRainAudio keep their caches warm while
// the context is suspended, so nothing jumps on resume.
export const audioReady = () => !!ctx;

export function setMasterVolume(v) {
  userVol = clamp01(v);
  if (ctx) masterGain.gain.setTargetAtTime(muted ? 0 : userVol, ctx.currentTime, 0.03);
}

export function toggleMute() {
  muted = !muted;
  if (ctx) masterGain.gain.setTargetAtTime(muted ? 0 : userVol, ctx.currentTime, 0.03);
  try { localStorage.setItem('ugh3d.muted', muted ? '1' : '0'); } catch (e) { /* private mode */ }
  return !muted;                                   // true = sound is ON
}

export function setBus(name, v) {
  if (!ctx) return;
  const b = name === 'amb' ? ambBus : name === 'ui' ? uiBus : sfxBus;
  BUS_BASE[name] = v;
  b.gain.setTargetAtTime(v, ctx.currentTime, 0.05);
}

// ------------------------------------------------------------------ hall
// Feedback-delay network instead of a ConvolverNode: an impulse response long
// enough for this hall costs 1-3 % CPU on top of the world we already draw.
function buildReverb() {
  revIn = ctx.createBiquadFilter();
  revIn.type = 'highpass'; revIn.frequency.value = 180;   // keep T-rex sub out of the tail
  const sum = ctx.createGain(); sum.gain.value = 1;
  for (const dt of [0.0297, 0.0371, 0.0411, 0.0437]) {
    const d = ctx.createDelay(0.2); d.delayTime.value = dt;
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 2600;
    const fb = ctx.createGain(); fb.gain.value = 0.62;     // RT60 ~1.5 s, well short of self-oscillation
    revIn.connect(d); d.connect(lp); lp.connect(fb); fb.connect(d);
    d.connect(sum);
  }
  const trim = ctx.createGain(); trim.gain.value = 0.6;
  sum.connect(trim);
  let node = trim;
  for (const dt of [0.0050, 0.0170]) {                     // two diffusers smear the comb ring
    const d = ctx.createDelay(0.05); d.delayTime.value = dt;
    const fb = ctx.createGain(); fb.gain.value = 0.6;
    node.connect(d); d.connect(fb); fb.connect(d);
    node = d;
  }
  revLP = ctx.createBiquadFilter(); revLP.type = 'lowpass'; revLP.frequency.value = 2200;
  revGain = ctx.createGain(); revGain.gain.value = 0.28;
  node.connect(revLP); revLP.connect(revGain); revGain.connect(masterGain);
}

const room = { f: -1, base: -1 };

// altFrac 0..1 = height under the leaf roof, wet 0..1, inCrown = hiding in a crown.
export function setRoom(altFrac, wet = 0, inCrown = false) {
  if (!ctx) return;
  let f = 1400 + clamp01(altFrac) * 2400;
  let base = 0.18 + clamp01(wet) * 0.08;
  if (inCrown) { base *= 0.55; f = Math.min(f, 1100); }    // a crown is dark and close
  if (Math.abs(f - room.f) > 20) { room.f = f; revLP.frequency.setTargetAtTime(f, ctx.currentTime, 0.4); }
  if (Math.abs(base - room.base) > 0.004) { room.base = base; sendScale = base / 0.18; }
}

// Ducking lives on the buses, NEVER on rotorGain — setRotor writes that every
// frame and the two automations would fight.
function duckBus(bus, base, amount, attack, hold, release) {
  const t = ctx.currentTime;
  bus.gain.cancelScheduledValues(t);
  bus.gain.setTargetAtTime(base * (1 - amount), t, attack);
  bus.gain.setTargetAtTime(base, t + hold, release);
}

function duck(amount, attack, hold, release) {
  if (!ctx) return;
  const a = clamp(amount, 0, 0.9);
  duckBus(ambBus, BUS_BASE.amb, a, attack, hold, release);
  duckBus(sfxBus, BUS_BASE.sfx, a * 0.3, attack, hold, release);
  // uiBus is never ducked: coin feedback must survive a thunderclap
}

// ------------------------------------------------------------- primitives
function busOf(name) { return name === 'amb' ? ambBus : name === 'ui' ? uiBus : sfxBus; }

// Connect a voice's output: either into a prebuilt spatial chain, or straight
// to its bus with an optional pan and reverb send. Returns the nodes it made
// so the caller can disconnect them when the source ends.
function route(node, o = {}) {
  const made = [];
  if (o.node) { node.connect(o.node); return made; }
  let out = node;
  if (o.pan) {
    const p = ctx.createStereoPanner(); p.pan.value = clamp(o.pan, -1, 1);
    out.connect(p); made.push(p); out = p;
  }
  if (o.dry !== 0) out.connect(busOf(o.bus));
  const s = (o.send || 0) * sendScale;
  if (s > 0) {
    const sg = ctx.createGain(); sg.gain.value = s;
    out.connect(sg); sg.connect(revIn); made.push(sg);
  }
  return made;
}

function killer(src, nodes) {
  src.onended = () => { for (const n of nodes) { try { n.disconnect(); } catch (e) { /* already gone */ } } };
}

function blip(freq0, freq1, dur, type = 'sine', gain = 0.25, when = 0, o = {}) {
  if (!ctx) return;
  const t = ctx.currentTime + when;
  const osc = ctx.createOscillator(); osc.type = type;
  osc.frequency.setValueAtTime(freq0, t);
  osc.frequency.exponentialRampToValueAtTime(Math.max(1, freq1), t + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(gain, t + Math.min(0.006, dur * 0.4));   // 6 ms: no click
  g.gain.exponentialRampToValueAtTime(0.001, t + dur);
  osc.connect(g);
  const made = route(g, o);
  osc.start(t); osc.stop(t + dur + 0.05);
  killer(osc, [osc, g, ...made]);
}

// shape: 'burst' (default, snappy), 'roll' (thunder), 'hiss' (held), 'sweep'.
// o: {type, q, f1, fdur, attack, bus, send, pan, node, dry}
function noiseBurst(dur, freq, gain = 0.5, when = 0, o = {}) {
  if (!ctx) return;
  const t = ctx.currentTime + when;
  const shape = o.shape || 'burst';
  const src = ctx.createBufferSource();
  src.buffer = NOISE;
  src.playbackRate.value = 0.7 + Math.random() * 0.6;
  const f = ctx.createBiquadFilter();
  f.type = o.type || 'lowpass';
  f.frequency.setValueAtTime(Math.max(20, freq), t);
  if (o.q) f.Q.value = o.q;
  if (o.f1) f.frequency.exponentialRampToValueAtTime(Math.max(20, o.f1), t + (o.fdur || dur));
  const g = ctx.createGain(); g.gain.setValueAtTime(0.0001, t);
  src.connect(f); f.connect(g);
  const made = route(g, o);

  let end = t + dur;
  const atk = o.attack || 0.003;
  if (shape === 'roll') {                                  // slow swell: thunder, roars
    g.gain.linearRampToValueAtTime(gain, t + 0.18);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  } else if (shape === 'hiss') {                           // hold, then let go
    g.gain.linearRampToValueAtTime(gain, t + Math.min(0.6, dur));
    g.gain.setValueAtTime(gain, t + dur);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur + 0.4);
    end = t + dur + 0.4;
  } else if (shape === 'sweep') {                          // splash impact
    f.frequency.setValueAtTime(5000, t);
    f.frequency.exponentialRampToValueAtTime(400, t + dur);
    g.gain.linearRampToValueAtTime(gain, t + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  } else {
    g.gain.linearRampToValueAtTime(gain, t + atk);
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  }
  const play = end - t + 0.02;
  src.start(t, Math.random() * Math.max(0, 3.9 - play), play);
  src.stop(end + 0.05);
  killer(src, [src, f, g, ...made]);
}

// --------------------------------------------------------------- spatial
let lx = 0, lz = 0, lyaw = 0, frameNo = 0;
let chains = [];

export function setListener(px, pz, yaw) {
  lx = px; lz = pz; lyaw = yaw;
  frameNo++;
  if ((frameNo & 7) === 0) rankRemotes();
}

// StereoPanner + one lowpass per voice: equal-power panning and air/leaf
// absorption for ~nothing. HRTF convolution per voice would hurt on mobile.
function spatial(x, z, o = {}) {
  const dx = x - lx, dz = z - lz;
  const d = Math.hypot(dx, dz);
  const rel = Math.atan2(dx, dz) - lyaw;
  // sign verified against a projected test point in the right-handed scene:
  // cross(forward, up) lands on the RIGHT of the screen and gives sin(rel) = -1,
  // so screen-right is -sin(rel). 0.7 because 0.85 ping-pongs in headphones.
  const pan = ctx.createStereoPanner();
  pan.pan.value = clamp(-Math.sin(rel) * 0.7, -1, 1);
  const lp = ctx.createBiquadFilter(); lp.type = 'lowpass';
  lp.frequency.value = clamp(18000 * Math.exp(-d / 220), 400, 18000);
  const g = ctx.createGain();
  g.gain.value = 1 / (1 + d / 45);
  let out = pan;
  if (Math.cos(rel) < 0) {                                 // head shadow kills front/back confusion
    const bk = ctx.createBiquadFilter(); bk.type = 'lowpass'; bk.frequency.value = 4000;
    pan.connect(bk); out = bk;
    g.gain.value *= 0.75;
  }
  out.connect(lp); lp.connect(g); g.connect(busOf(o.bus));
  const made = [pan, lp, g];
  if (out !== pan) made.push(out);
  const s = (o.send || 0) * sendScale;
  if (s > 0) {                                             // tapped pre-distance: far = wetter
    const sg = ctx.createGain(); sg.gain.value = s;
    pan.connect(sg); sg.connect(revIn);
    made.push(sg);
  }
  // one-shots disconnect themselves; the chain they fed has no owner, so sweep
  // it long after the longest voice through it can still be sounding
  chains.push({ n: made, at: ctx.currentTime + 6 });
  return pan;
}

function sweepChains(now) {
  if (chains.length === 0) return;
  const keep = [];
  for (const c of chains) {
    if (c.at > now) { keep.push(c); continue; }
    for (const n of c.n) { try { n.disconnect(); } catch (e) { /* gone */ } }
  }
  chains = keep;
}

// Options for a positioned one-shot. Pass the SAME object to every layer of a
// sound so they share one chain.
function place(x, z, send = 0, bus = 'sfx') {
  if (!ctx) return {};
  if (typeof x !== 'number' || typeof z !== 'number' || !isFinite(x)) return { send, bus };
  return { node: spatial(x, z, { send, bus }) };
}

function caveXZ(cave) {
  if (cave === null || cave === undefined) return null;
  const c = CAVES.find(v => v.id === cave) || CAVES[cave | 0];
  return c ? c : null;
}

// Passenger ids may be numbers or strings — hash either into one stable voice.
function seeded(seed) {
  const s = String(seed === undefined || seed === null ? 0 : seed);
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619);
  return mulberry32(h >>> 0)();
}

// ---------------------------------------------------------------- rotor
let rotorBus = null, rotorSrc = null, bodyLP = null, bodyGain = null;
let chop = null, chop2 = null, tipBP = null, tipGain = null;
let shaftOsc = null, shaftGain = null, bladePulse = null;
let sputterLFO = null, sputterDepth = null;
let crankPhase = 0;
const wrote = {};

function buildRotor() {
  rotorBus = ctx.createGain(); rotorBus.gain.value = 1; rotorBus.connect(ambBus);
  const send = ctx.createGain(); send.gain.value = 0.06;
  rotorBus.connect(send); send.connect(revIn);

  // one looping noise source at rate 1.0 — resampling it made a comb that beat
  // against the blade rate; the chop now comes from a real periodic wave
  rotorSrc = ctx.createBufferSource(); rotorSrc.buffer = NOISE; rotorSrc.loop = true;
  rotorSrc.playbackRate.value = 1.0;

  // blade pulse: 8 harmonics so each blade passage really closes between hits
  const re = new Float32Array(9), im = new Float32Array(9);
  const H = [1, 0.72, 0.50, 0.34, 0.22, 0.14, 0.09, 0.05];
  for (let i = 0; i < H.length; i++) im[i + 1] = H[i];
  bladePulse = ctx.createOscillator();
  bladePulse.setPeriodicWave(ctx.createPeriodicWave(re, im, { disableNormalization: false }));
  bladePulse.frequency.value = 0.8;
  const pulseAmt = ctx.createGain(); pulseAmt.gain.value = 0.41;
  bladePulse.connect(pulseAmt);

  chop = ctx.createGain(); chop.gain.value = 0.59;          // 0.18..1.0, never strobing
  pulseAmt.connect(chop.gain);

  bodyLP = ctx.createBiquadFilter(); bodyLP.type = 'lowpass';
  bodyLP.frequency.value = 120; bodyLP.Q.value = 1.4;
  bodyGain = ctx.createGain(); bodyGain.gain.value = 0;
  rotorSrc.connect(bodyLP); bodyLP.connect(chop); chop.connect(bodyGain); bodyGain.connect(rotorBus);

  // blade-tip whoosh: the missing 1-3.5 kHz, phase-shifted off the body chop
  tipBP = ctx.createBiquadFilter(); tipBP.type = 'bandpass';
  tipBP.frequency.value = 900; tipBP.Q.value = 0.7;
  chop2 = ctx.createGain(); chop2.gain.value = 0.59;
  const pulseDelay = ctx.createDelay(0.05); pulseDelay.delayTime.value = 0.012;
  const pulseAmt2 = ctx.createGain(); pulseAmt2.gain.value = 0.41;
  bladePulse.connect(pulseDelay); pulseDelay.connect(pulseAmt2); pulseAmt2.connect(chop2.gain);
  tipGain = ctx.createGain(); tipGain.gain.value = 0;
  rotorSrc.connect(tipBP); tipBP.connect(chop2); chop2.connect(tipGain); tipGain.connect(rotorBus);

  // shaft hum
  shaftOsc = ctx.createOscillator(); shaftOsc.type = 'sine'; shaftOsc.frequency.value = 28;
  shaftGain = ctx.createGain(); shaftGain.gain.value = 0;
  shaftOsc.connect(shaftGain); shaftGain.connect(rotorBus);

  // engine cough (out of energy): 6 Hz tremolo on the whole rotor bus
  sputterLFO = ctx.createOscillator(); sputterLFO.type = 'sine'; sputterLFO.frequency.value = 6;
  sputterDepth = ctx.createGain(); sputterDepth.gain.value = 0;
  sputterLFO.connect(sputterDepth); sputterDepth.connect(rotorBus.gain);

  rotorSrc.start(); bladePulse.start(); shaftOsc.start(); sputterLFO.start();
}

// Write an AudioParam only when the target actually moved: setRotor runs every
// frame and rewriting a setTargetAtTime curve restarts it, which made every
// time constant in here meaningless (~300 events/s).
function wr(param, key, val, eps, tau, t) {
  if (wrote[key] !== undefined && Math.abs(val - wrote[key]) < eps) return;
  wrote[key] = val;
  param.setTargetAtTime(val, t, tau);
}

const energyState = { energy: 100, pedaling: false, sputter: false, reserve: false };

// setRotor(speed, pedaling, { vy, wet, dt }) — the old two-argument call still works.
export function setRotor(speed, pedaling, o = {}) {
  if (!ctx) return;
  const t = ctx.currentTime;
  const s = clamp(speed || 0, 0, 1.2);
  const vy = o.vy || 0, wet = clamp01(o.wet || 0), dt = o.dt || 0.016;

  // crank phase: two stick-slip events per revolution, one per foot (max 3.82/s)
  if (pedaling) {
    crankPhase += s * (CRANK_W / (2 * Math.PI)) * 2 * dt;
    let guard = 0;
    while (crankPhase >= 1 && guard++ < 4) { crankPhase -= 1; creakEvent(wet); }
  } else if (wrote.pedaling) crankPhase = 0;
  wrote.pedaling = pedaling;

  const dead = s < 0.02;                              // D1: the rotor really can hit zero
  const tau = dead ? 0.08 : 0.12;
  const dop = 1 + clamp(-vy / 40, -0.12, 0.12);       // dive = a touch of doppler lift
  const cough = energyState.sputter ? 0.85 : 1;
  const bladeHz = (0.8 + s * (BLADE_HZ_MAX - 0.8)) * dop * cough;

  wr(bladePulse.frequency, 'bhz', bladeHz, 0.12, dead ? 0.06 : 0.12, t);
  wr(bodyLP.frequency, 'blp', 120 + s * 260, 6, tau, t);
  wr(tipBP.frequency, 'tbp', (900 + s * 2600) * dop, 40, tau, t);
  wr(shaftOsc.frequency, 'shz', 28 + s * 26, 1, tau, t);
  wr(bodyGain.gain, 'bg', dead ? 0 : s * 0.22, 0.004, tau, t);
  wr(tipGain.gain, 'tg', dead ? 0 : s * s * 0.09, 0.003, tau, t);
  wr(shaftGain.gain, 'sg', dead ? 0 : s * 0.05, 0.002, tau, t);
}

// Pedal stick-slip: one squeak per foot, locked to the crank the player sees.
function creakEvent(wet) {
  const r = Math.random();
  noiseBurst(0.16, 340, 0.055, 0, {
    type: 'bandpass', q: 9 + r * 8, f1: 780 * (0.85 + r * 0.3), fdur: 0.13,
    attack: 0.008, bus: 'amb', send: 0.06,
  });
  blip(130, 128, 0.07, 'sine', 0.04, 0, { bus: 'amb', send: 0.06 });     // strap thump
  if (wet > 0.3) {                                    // wet pedals slip — you can hear the penalty
    noiseBurst(0.09, 1100, 0.03 * wet, 0, { type: 'bandpass', q: 4, bus: 'amb' });
  }
  if (energyState.reserve) {                          // Cavelon gasping on the reserve tank
    noiseBurst(0.34, 620, 0.06, 0, { type: 'bandpass', q: 1.8, attack: 0.09, bus: 'sfx' });
  }
}

// ----------------------------------------------------------------- rain
let rainGain = null, rainLP = null, rainLFO = null;
let wetNow = 0;
const rainCache = { level: -1 };

function buildRain() {
  const src = ctx.createBufferSource(); src.buffer = NOISE; src.loop = true;
  src.playbackRate.value = 0.93;
  const hp = ctx.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 900;
  rainLP = ctx.createBiquadFilter(); rainLP.type = 'lowpass'; rainLP.frequency.value = 6500;
  // the one thing that separates rain from tape hiss: a slow breathing top end
  rainLFO = ctx.createOscillator(); rainLFO.type = 'sine'; rainLFO.frequency.value = 0.23;
  const lfoG = ctx.createGain(); lfoG.gain.value = 1700;
  rainLFO.connect(lfoG); lfoG.connect(rainLP.frequency);
  rainGain = ctx.createGain(); rainGain.gain.value = 0;
  src.connect(hp); hp.connect(rainLP); rainLP.connect(rainGain); rainGain.connect(ambBus);
  src.start(); rainLFO.start();
}

export function setRainAudio(level) {
  if (!ctx) return;
  wetNow = clamp01(level);
  if (Math.abs(wetNow - rainCache.level) < 0.01) return;
  rainCache.level = wetNow;
  rainGain.gain.setTargetAtTime(wetNow * 0.16, ctx.currentTime, 0.5);
  ambienceState({ wet: wetNow });
}

// ------------------------------------------------------------- ambience
let windGain = null, dc = null;
let insectAmp = null, insectOff = null, insectSwing = null;
let riverGain = null, riverLP = null, washDepth = null;
let schedTimer = null;
let nextBirdAt = 0, nextDripAt = 0, nextHeartAt = 0, nextHullAt = 0;
let heartBeats = 0, heartFrozen = 0, hullDamage = 0, quietUntil = 0;
let birdsOn = true, insectScale = 1;

function startAmbience() {
  dc = ctx.createConstantSource(); dc.offset.value = 1; dc.start();

  // 1. hall wind: the sealed valley always breathes a little
  const wsrc = ctx.createBufferSource(); wsrc.buffer = NOISE; wsrc.loop = true;
  wsrc.playbackRate.value = 0.6;
  const wlp = ctx.createBiquadFilter(); wlp.type = 'lowpass';
  wlp.frequency.value = 380; wlp.Q.value = 0.5;
  windGain = ctx.createGain(); windGain.gain.value = 0;
  const wOff = ctx.createGain(); wOff.gain.value = 0.014;
  dc.connect(wOff); wOff.connect(windGain.gain);
  const wlfo = ctx.createOscillator(); wlfo.type = 'sine'; wlfo.frequency.value = 0.07;
  const wlfoG = ctx.createGain(); wlfoG.gain.value = 0.008;
  wlfo.connect(wlfoG); wlfoG.connect(windGain.gain);
  wsrc.connect(wlp); wlp.connect(windGain); windGain.connect(ambBus);
  const wsend = ctx.createGain(); wsend.gain.value = 0.4;
  windGain.connect(wsend); wsend.connect(revIn);
  wsrc.start(); wlfo.start();

  // 2. cicadas: real 24/31 Hz wing rates, and the chorus swells to silence and
  // back so 4-5 kHz never becomes fatiguing over a 20-minute session
  insectAmp = ctx.createGain(); insectAmp.gain.value = 0;
  insectAmp.connect(ambBus);
  insectOff = ctx.createGain(); insectOff.gain.value = 0.006;
  dc.connect(insectOff); insectOff.connect(insectAmp.gain);
  const slfo = ctx.createOscillator(); slfo.type = 'sine'; slfo.frequency.value = 0.05;
  insectSwing = ctx.createGain(); insectSwing.gain.value = 0.006;
  slfo.connect(insectSwing); insectSwing.connect(insectAmp.gain);
  slfo.start();
  [[4200, 24], [5100, 31]].forEach(([f, rate], i) => {
    const o = ctx.createOscillator(); o.type = 'square'; o.frequency.value = f;
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass';
    bp.frequency.value = f; bp.Q.value = 18;
    const am = ctx.createGain(); am.gain.value = 0.5;
    const lfo = ctx.createOscillator(); lfo.type = 'sine'; lfo.frequency.value = rate;
    const lg = ctx.createGain(); lg.gain.value = 0.5;
    lfo.connect(lg); lg.connect(am.gain);
    const pan = ctx.createStereoPanner(); pan.pan.value = i ? 0.45 : -0.45;
    o.connect(bp); bp.connect(am); am.connect(pan); pan.connect(insectAmp);
    o.start(); lfo.start();
  });

  // 5. river: highpassed noise with a wandering lowpass — that wander is the
  // difference between a burbling river and white noise
  const rsrc = ctx.createBufferSource(); rsrc.buffer = NOISE; rsrc.loop = true;
  rsrc.playbackRate.value = 1.07;
  const rhp = ctx.createBiquadFilter(); rhp.type = 'highpass'; rhp.frequency.value = 240;
  riverLP = ctx.createBiquadFilter(); riverLP.type = 'lowpass'; riverLP.frequency.value = 1800;
  const rlfo = ctx.createOscillator(); rlfo.type = 'sine'; rlfo.frequency.value = 0.31;
  const rlfoG = ctx.createGain(); rlfoG.gain.value = 400;
  rlfo.connect(rlfoG); rlfoG.connect(riverLP.frequency);
  riverGain = ctx.createGain(); riverGain.gain.value = 0;
  rsrc.connect(rhp); rhp.connect(riverLP); riverLP.connect(riverGain); riverGain.connect(ambBus);
  // downwash: low over the water the rotor whips the surface IN TIME with the blades
  const wash = ctx.createBiquadFilter(); wash.type = 'bandpass';
  wash.frequency.value = 3200; wash.Q.value = 1.2;
  const washGain = ctx.createGain(); washGain.gain.value = 0;
  washDepth = ctx.createGain(); washDepth.gain.value = 0;
  bladePulse.connect(washDepth); washDepth.connect(washGain.gain);
  rhp.connect(wash); wash.connect(washGain); washGain.connect(ambBus);
  rsrc.start(); rlfo.start();

  nextBirdAt = ctx.currentTime + 2.5;
  nextDripAt = ctx.currentTime + 1;
}

// Wet jungle: the insects thin out and the birds shut up entirely.
export function ambienceState({ wet = 0 } = {}) {
  if (!ctx) return;
  insectScale = wet > 0.5 ? 0.15 : 1;
  birdsOn = wet <= 0.5;
  const t = ctx.currentTime;
  insectOff.gain.setTargetAtTime(0.006 * insectScale, t, 1.5);
  insectSwing.gain.setTargetAtTime(0.006 * insectScale, t, 1.5);
}

// The jungle holds its breath after something big happens.
export function startle(sec = 6) {
  if (!ctx) return;
  quietUntil = ctx.currentTime + sec;
  insectOff.gain.setTargetAtTime(0.0005, ctx.currentTime, 0.15);
  insectOff.gain.setTargetAtTime(0.006 * insectScale, ctx.currentTime + sec, 1.2);
}

const BIRD_MOTIFS = [0, 1, 2, 3];

// Lookahead scheduler: background tabs throttle timers, so everything timed
// here is placed on ctx.currentTime one second ahead instead of via setTimeout.
function schedTick() {
  if (!ctx || ctx.state === 'suspended') return;
  const now = ctx.currentTime, horizon = now + 1.0;

  if (nextBirdAt < now) nextBirdAt = now + 0.2;
  while (nextBirdAt < horizon) {
    if (birdsOn && nextBirdAt > quietUntil) birdCall(nextBirdAt - now);
    nextBirdAt += 4 + Math.random() * 7;
  }

  if (nextDripAt < now) nextDripAt = now + 0.1;
  while (nextDripAt < horizon) {
    if (wetNow > 0.4) {                                // fat drops off the leaves
      noiseBurst(0.06, 2600, 0.04, nextDripAt - now, {
        type: 'bandpass', q: 3, bus: 'amb', pan: Math.random() * 1.6 - 0.8, send: 0.3,
      });
    }
    nextDripAt += 0.12 + Math.random() * 0.28;
  }

  scheduleHeart(now, horizon);
  scheduleHull(now, horizon);
  sweepChains(now);
}

function birdCall(when) {
  const m = BIRD_MOTIFS[(Math.random() * 4) | 0];
  const n = 2 + ((Math.random() * 3) | 0);
  const pan = Math.random() * 1.8 - 0.9;
  const f = 1600 + m * 380 + Math.random() * 600;
  for (let i = 0; i < n; i++) {
    const up = m % 2 ? 1 + i * 0.18 : 1 - i * 0.09;
    // 100 % wet: the hall alone tells you the bird is far away
    blip(f * up, f * up * (m > 1 ? 1.3 : 0.8), 0.06 + Math.random() * 0.05, 'sine', 0.05,
      when + i * 0.09, { send: 1, dry: 0, pan, bus: 'amb' });
  }
}

const waterCache = { g: -1, w: -1 };

export function setWaterAudio(dRiver, alt) {
  if (!ctx) return;
  const d = Math.abs(dRiver || 0), a = alt || 0;
  const g = 0.10 * Math.exp(-((d / 45) * (d / 45))) * clamp01(1 - a / 34);
  if (Math.abs(g - waterCache.g) > 0.002) {
    waterCache.g = g;
    riverGain.gain.setTargetAtTime(g, ctx.currentTime, 0.35);
  }
  const w = (d < 30 && a < 12) ? 0.03 : 0;
  if (w !== waterCache.w) {
    waterCache.w = w;
    washDepth.gain.setTargetAtTime(w, ctx.currentTime, 0.35);
  }
}

// ---------------------------------------------------------------- voice
// One guttural engine, four uses: the shout, boarding, delivery, and the
// customer who gave up. Sawtooth -> throat rasp -> three formants.
function voice(o = {}) {
  if (!ctx) return;
  const t = ctx.currentTime + (o.when || 0);
  const f0 = o.f0 || 96, dur = o.dur || 0.32;
  const osc = ctx.createOscillator(); osc.type = 'sawtooth';
  const p0 = o.p0 === undefined ? 1.25 : o.p0;
  const p1 = o.p1 === undefined ? 1.0 : o.p1;
  const p2 = o.p2 === undefined ? 0.72 : o.p2;
  osc.frequency.setValueAtTime(Math.max(20, f0 * p0), t);
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, f0 * p1), t + Math.min(0.05, dur * 0.3));
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, f0 * p2), t + dur * 0.82);

  const vib = ctx.createOscillator(); vib.type = 'sine'; vib.frequency.value = 5.5;
  const vibG = ctx.createGain(); vibG.gain.value = 22;
  vib.connect(vibG); vibG.connect(osc.detune);

  const rasp = ctx.createWaveShaper(); rasp.curve = RASP;
  osc.connect(rasp);

  const sum = ctx.createGain();
  const F = o.formants || [[700, 6.5, 1.0], [1150, 9.0, 0.55], [2450, 11, 0.22]];
  const fto = o.fTo || [520, 900];                    // U -> GH: the mouth closes
  const made = [];
  F.forEach((fm, i) => {
    const bp = ctx.createBiquadFilter(); bp.type = 'bandpass';
    bp.frequency.setValueAtTime(fm[0], t);
    if (fto[i]) bp.frequency.setTargetAtTime(fto[i], t + 0.05, 0.06);
    bp.Q.value = Math.min(11, fm[1]);                 // above 11 it self-resonates
    const g = ctx.createGain(); g.gain.value = fm[2];
    rasp.connect(bp); bp.connect(g); g.connect(sum);
    made.push(bp, g);
  });

  const amp = o.gain === undefined ? 0.42 : o.gain;
  sum.gain.setValueAtTime(0.0001, t);
  sum.gain.linearRampToValueAtTime(amp, t + 0.012);
  if (o.syllables === 2) {
    sum.gain.exponentialRampToValueAtTime(amp * 0.22, t + dur * 0.45);
    sum.gain.linearRampToValueAtTime(amp * 0.92, t + dur * 0.58);
  }
  sum.gain.exponentialRampToValueAtTime(0.001, t + dur);
  made.push(...route(sum, o));

  osc.start(t); osc.stop(t + dur + 0.05);
  vib.start(t); vib.stop(t + dur + 0.05);
  killer(osc, [osc, rasp, sum, vib, vibG, ...made]);

  if (o.gh) {                                         // the closing "GH"
    noiseBurst(0.15, 1400, 0.10, (o.when || 0) + Math.min(0.19, dur * 0.6), {
      type: 'bandpass', q: 2, attack: 0.02, node: o.node, bus: o.bus, send: o.send,
    });
  }
}

// "UGH!" — every customer gets their own voice, seeded on the passenger id.
export function sfxUgh(seed = 0, cave = null) {
  if (!ctx) return;
  const c = caveXZ(cave);
  const f0 = 96 * (0.86 + seeded(seed) * 0.28);       // 82-109 Hz
  const o = c ? place(c.x, c.z, 0.30) : { send: 0.30 };
  voice({ ...o, f0, dur: 0.34, gh: true });
}

// kind: 'board' | 'deliver' | 'left'
export function sfxPaxMood(kind, seed = 0, cave = null) {
  if (!ctx) return;
  const c = caveXZ(cave);
  const base = 96 * (0.86 + seeded(seed) * 0.28);
  const o = c ? place(c.x, c.z, 0.28) : { send: 0.28 };
  if (kind === 'deliver') {                           // a two-syllable thank-you
    voice({
      ...o, f0: base, dur: 0.30, gain: 0.36, syllables: 2, p0: 1, p1: 1, p2: 1.3,
      formants: [[700, 6.5, 1.0], [1150, 9.0, 0.5], [2450, 11, 0.2]], fTo: [820, 1250],
    });
  } else if (kind === 'left') {                       // stomping back into the cave
    voice({
      ...o, f0: 110, dur: 0.5, gain: 0.38, p0: 1, p1: 1, p2: 0.64,
      formants: [[700, 6.0, 1.0], [1100, 8.0, 0.5], [2300, 10, 0.18]], fTo: [480, 900],
    });
    blip(70, 62, 0.12, 'sine', 0.12, 0.30, o);
    blip(70, 60, 0.12, 'sine', 0.10, 0.46, o);
  } else {                                            // 'board': a contented grunt
    voice({
      ...o, f0: base * 1.15, dur: 0.14, gain: 0.32, p0: 1.1, p1: 1, p2: 0.88,
      formants: [[620, 6.0, 1.0], [1050, 8.5, 0.5], [2400, 10, 0.18]], fTo: [560, 980],
    });
  }
}

// kept so nothing breaks while main.js is being wired to sfxPaxMood
export function sfxBoard() { sfxPaxMood('board', 0, null); }

// ------------------------------------------------------------ one-shots
export function sfxCoin(n = 1, tip = false, q = 0) {
  const c = Math.min(n, 5);
  const base = 660;
  for (let i = 0; i < c; i++) {
    blip(base * (1 + i * 0.25), base * (1 + i * 0.25) * 1.5, 0.12, 'sine', 0.16, i * 0.08,
      { bus: 'ui', send: 0.20 });
  }
  if (!tip) return;
  // the tip trill hangs on AFTER the coins; its top note rises with the ride
  // quality so a 4 % tip and a 50 % tip are audibly different
  const start = c * 0.08;
  const top = 1319 * (0.94 + 0.10 * clamp01(q));
  [784, 988, top].forEach((f, i) =>
    blip(f, f * 1.01, 0.16, 'triangle', 0.10 + 0.08 * clamp01(q), start + i * 0.10,
      { bus: 'ui', send: 0.35 }));
}

// repair fee: the coin sound, downcast and falling
export function sfxFee() {
  [523, 392, 294].forEach((f, i) =>
    blip(f, f * 0.99, 0.18, 'triangle', 0.10, i * 0.09, { bus: 'ui', send: 0.2 }));
}

export function sfxEat() {
  blip(300, 700, 0.1, 'sine', 0.20, 0, { send: 0.15 });
  blip(500, 200, 0.12, 'triangle', 0.12, 0.06, { send: 0.15 });
}

export function sfxCrash(x, z) {
  const o = place(x, z, 0.50);
  duck(0.7, 0.01, 0.5, 0.3);
  noiseBurst(0.6, 900, 0.34, 0, { ...o, shape: 'roll' });
  blip(120, 30, 0.5, 'sawtooth', 0.22, 0, o);
}

// Four layers, and the bubble RISES — a falling tone reads as a dying synth.
export function sfxSplash(x, z) {
  const o = place(x, z, 0.60);
  duck(0.7, 0.01, 0.5, 0.3);
  noiseBurst(0.25, 5000, 0.45, 0, { ...o, shape: 'sweep' });   // the hit
  blip(180, 900, 0.35, 'sine', 0.12, 0.02, o);                 // the bubble coming up
  for (let i = 0; i < 6; i++) {                                // scattered droplets
    blip(2200 + Math.random() * 1600, 1800, 0.03, 'sine', 0.04,
      0.15 + Math.random() * 0.75, { bus: 'sfx', send: 0.5, pan: Math.random() * 1.6 - 0.8 });
  }
}

export function sfxSquawk(x, z) {
  const o = place(x, z, 0.30);
  blip(1400, 500, 0.25, 'sawtooth', 0.18, 0, o);
  blip(1100, 400, 0.2, 'square', 0.10, 0.12, o);
}

export function sfxThud(v = 0.3, x, z) {
  const o = place(x, z, 0.30);
  noiseBurst(0.15, 300, Math.min(0.4, v), 0, o);
  blip(80, 40, 0.15, 'sine', Math.min(0.4, v), 0, o);
}

// q = landScore(vy, vh) from shared/level.mjs: 0 = slam, 1 = feather.
export function sfxLand(q = 0, vy = 0) {
  if (!ctx) return;
  const k = clamp01(q);
  if (k >= 0.85) {                                    // skids settling — the feather landing
    blip(90, 88, 0.14, 'sine', 0.06, 0, { send: 0.2 });
    blip(90, 86, 0.14, 'sine', 0.06, 0.04, { send: 0.2 });
    noiseBurst(0.12, 420, 0.04, 0.02, { type: 'bandpass', q: 8, send: 0.2 });
  } else if (k >= 0.55) {                             // still well paid
    blip(110, 90, 0.16, 'sine', 0.08, 0, { send: 0.25 });
    noiseBurst(0.15, 900, 0.05, 0, { send: 0.25 });
  } else if (k >= 0.25) {                             // you can hear the tip shrinking
    blip(130, 90, 0.18, 'sine', 0.10, 0, { send: 0.3 });
    noiseBurst(0.2, 900, 0.08, 0, { send: 0.3 });
  } else {
    sfxThud(Math.min(0.5, Math.abs(vy) / 20));
  }
}

let idleAt = 0;

// Called every frame while parked; throttles itself.
export function sfxIdleTick() {
  if (!ctx || document.hidden) return;
  const t = ctx.currentTime;
  if (t < idleAt) return;
  idleAt = t + 2.5 + Math.random() * 2.5;
  blip(220, 210, 0.03, 'triangle', 0.03, 0, { bus: 'amb', send: 0.25 });
  noiseBurst(0.05, 700, 0.02, 0.01, { type: 'bandpass', q: 4, bus: 'amb', send: 0.25 });
}

// T-rex roar: sub drop + growl + rumble. vol 0..1 already carries the
// gameplay distance curve (1 - d/400); the spatial chain rides on top.
export function sfxRoar(vol = 1, x, z) {
  if (!ctx) return;
  const v = clamp(vol, 0.05, 1);
  const o = place(x, z, 0.55);
  duck(0.65 * v, 0.04, 0.9, 0.35);
  startle(6);
  blip(55, 28, 1.4, 'sine', 0.34 * v, 0, o);
  blip(180, 45, 1.2, 'sawtooth', 0.22 * v, 0, o);
  blip(240, 70, 0.9, 'square', 0.09 * v, 0.08, o);
  noiseBurst(1.3, 350, 0.18 * v, 0, { ...o, shape: 'roll' });
  noiseBurst(0.5, 900, 0.09 * v, 0.1, o);
}

export function sfxSnore(vol = 0.15, x, z) {
  if (!ctx) return;
  const o = place(x, z, 0.35);
  blip(85, 55, 0.5, 'sawtooth', vol, 0, o);
  blip(60, 80, 0.35, 'sine', vol * 0.7, 0.55, o);
}

export function sfxThrow() { noiseBurst(0.18, 2200, 0.16); blip(500, 200, 0.15, 'sine', 0.10); }
export function sfxRustle() { noiseBurst(0.3, 4500, 0.14, 0, { send: 0.2 }); }

export function sfxTangle() {
  blip(320, 110, 0.2, 'square', 0.16, 0, { send: 0.3 });
  blip(180, 70, 0.25, 'sawtooth', 0.13, 0.08, { send: 0.3 });
  noiseBurst(0.15, 1200, 0.10, 0, { send: 0.3 });
}

export function sfxThunder(vol = 1, x, z) {
  if (!ctx) return;
  const v = clamp(vol, 0.05, 1);
  const o = place(x, z, 0.70);
  duck(0.5 * v, 0.02, 0.7, 0.4);
  startle(6);
  noiseBurst(0.15, 3000, 0.24 * v, 0, o);                        // crack
  noiseBurst(1.6, 220, 0.28 * v, 0.06, { ...o, shape: 'roll' }); // rolling rumble
  blip(65, 28, 1.4, 'sine', 0.20 * v, 0.05, o);
}

export function sfxFanfare() {
  duck(0.55, 0.05, 1.1, 0.5);
  const notes = [330, 392, 440, 523, 659];
  notes.forEach((f, i) => blip(f, f * 1.01, 0.22, 'triangle', 0.20, i * 0.11, { bus: 'ui' }));
  [523, 659, 784].forEach(f => blip(f, f, 0.6, 'sine', 0.10, notes.length * 0.11, { bus: 'ui' }));
}

export function sfxZap() {
  blip(3200, 300, 0.28, 'sawtooth', 0.18, 0, { send: 0.4 });
  blip(1600, 90, 0.35, 'square', 0.13, 0.05, { send: 0.4 });
  noiseBurst(0.3, 5000, 0.16, 0, { send: 0.4 });
}

export function sfxBonk(x, z) {
  const o = place(x, z, 0.35);
  blip(420, 180, 0.09, 'square', 0.20, 0, o);
  blip(300, 120, 0.12, 'triangle', 0.16, 0.05, o);
  noiseBurst(0.1, 800, 0.13, 0, o);
}

export function sfxPickupStone() {
  blip(180, 320, 0.08, 'square', 0.16, 0, { send: 0.2 });
  blip(240, 160, 0.1, 'triangle', 0.12, 0.06, { send: 0.2 });
}

// ---------------------------------------------------------------- alerts
// A storm must not turn to mud: at most three one-shot warning cues at a time.
let cues = [];
function cueSlot(dur) {
  if (!ctx) return false;
  const t = ctx.currentTime;
  cues = cues.filter(e => e > t);
  if (cues.length >= 3) return false;
  cues.push(t + dur);
  return true;
}

// pterodactyl closing in — throttled PER BIRD by the caller
export function sfxScreech(vol = 0.3, x, z) {
  if (!cueSlot(0.34)) return;
  const v = clamp(vol, 0.05, 1);
  const o = place(x, z, 0.30);
  blip(1800, 700, 0.22, 'sawtooth', 0.26 * v, 0, o);
  noiseBurst(0.12, 3000, 0.14 * v, 0.02, o);
}

// the ground shaking before the roar. ms is the warning window (2200 today).
export function sfxGrowl(vol = 1, ms = 2000, x, z) {
  if (!cueSlot(ms / 1000)) return;
  const v = clamp(vol, 0.05, 1);
  const d = Math.max(0.3, ms / 1000);
  const o = place(x, z, 0.45);
  blip(70, 40, d, 'sine', 0.35 * v, 0, o);
  noiseBurst(d * 0.9, 300, 0.16 * v, 0, { ...o, shape: 'roll' });
}

// lightning charging: 900 ms, ending 50 ms before the strike (BOLT_WARN_MS 950)
export function sfxCharge() {
  if (!cueSlot(0.9)) return;
  blip(200, 1400, 0.9, 'triangle', 0.16, 0, { send: 0.25 });
  noiseBurst(0.9, 400, 0.10, 0, { f1: 5000, fdur: 0.9, attack: 0.7, send: 0.25 });
}

// generic "something is about to happen" — lianas, canopy
export function sfxHiss(v = 0.5) {
  if (!cueSlot(0.6)) return;
  noiseBurst(0.6, 1200, 0.12 * clamp(v, 0.1, 1), 0, {
    type: 'bandpass', q: 2, shape: 'hiss', send: 0.25,
  });
}

// ------------------------------------------------- energy & hull (D8)
// Both voices are 100 % dry — this is your airframe, not the hall — and their
// combined gain stays under 0.14 so the blade pulse is never masked.
export function setEnergyState({ energy = 100, pedaling = false, sputter = false, reserve = false } = {}) {
  if (!ctx) return;
  energyState.energy = energy;
  energyState.pedaling = pedaling;
  energyState.sputter = sputter;
  energyState.reserve = reserve;
  const depth = reserve ? 0.55 : sputter ? 0.3 : 0;
  if (wrote.sputterDepth !== depth) {
    wrote.sputterDepth = depth;
    const t = ctx.currentTime;
    sputterDepth.gain.setTargetAtTime(depth, t, 0.1);
    rotorBus.gain.setTargetAtTime(1 - depth, t, 0.1);   // LFO swings it back up to 1
  }
  if (energy >= 25) { heartBeats = 0; heartFrozen = 0; }
}

function scheduleHeart(now, horizon) {
  const e = energyState.energy;
  if (e >= 25) { nextHeartAt = now + 0.5; return; }
  if (nextHeartAt < now) nextHeartAt = now + 0.1;
  while (nextHeartAt < horizon) {
    const when = nextHeartAt - now;
    blip(58, 54, 0.12, 'sine', 0.14, when, { dry: 1 });
    blip(44, 40, 0.12, 'sine', 0.11, when + 0.16, { dry: 1 });
    heartBeats++;
    // freeze the interval after a dozen beats — hovering by a fruit for a
    // minute should not end in a machine-gun pulse
    if (heartBeats >= 12) {
      if (!heartFrozen) heartFrozen = 1.4 - (25 - e) / 25 * 0.9;
      nextHeartAt += heartFrozen;
    } else {
      nextHeartAt += Math.max(0.5, 1.4 - (25 - clamp(e, 0, 25)) / 25 * 0.9);
    }
  }
}

export function setHullStress(damage) { hullDamage = damage || 0; }

function scheduleHull(now, horizon) {
  if (hullDamage <= 45) { nextHullAt = now + 1; return; }
  if (nextHullAt < now) nextHullAt = now + 0.2;
  while (nextHullAt < horizon) {
    const when = nextHullAt - now;
    const t = ctx.currentTime + when;
    const f = 70 + Math.random() * 50;
    const g = 0.05 + hullDamage / 100 * 0.09;
    // groaning timber: an uneven 7 Hz vibrato on a lowpassed saw
    const osc = ctx.createOscillator(); osc.type = 'sawtooth'; osc.frequency.value = f;
    const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 400;
    const vib = ctx.createOscillator(); vib.type = 'sine'; vib.frequency.value = 7;
    const vg = ctx.createGain(); vg.gain.value = 35;
    vib.connect(vg); vg.connect(osc.detune);
    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.0001, t);
    gain.gain.linearRampToValueAtTime(g, t + 0.06);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.5);
    osc.connect(lp); lp.connect(gain); gain.connect(sfxBus);
    osc.start(t); osc.stop(t + 0.56); vib.start(t); vib.stop(t + 0.56);
    killer(osc, [osc, lp, gain, vib, vg]);
    blip(f * 1.5, f * 1.4, 0.4, 'triangle', g * 0.4, when, { dry: 1 });   // the ring of the wood
    if (hullDamage > 80) {                                                // nails letting go
      noiseBurst(0.18, 1800, 0.05, when + 0.12, { type: 'bandpass', q: 14, f1: 2600, fdur: 0.18 });
    }
    nextHullAt += Math.max(0.8, 3.5 - hullDamage / 100 * 2.2);
  }
}

export function resetSurvivalAudio() {
  hullDamage = 0;
  heartBeats = 0; heartFrozen = 0;
  energyState.energy = 100; energyState.sputter = false; energyState.reserve = false;
  if (!ctx) return;
  wrote.sputterDepth = 0;
  sputterDepth.gain.setTargetAtTime(0, ctx.currentTime, 0.1);
  rotorBus.gain.setTargetAtTime(1, ctx.currentTime, 0.1);
  nextHeartAt = ctx.currentTime + 1;
  nextHullAt = ctx.currentTime + 1;
}

// ------------------------------------------------------- remote players
// One simplified rotor per remote taxi (body + blade pulse, no tip whoosh).
// Hard cap: only the four nearest inside 160 m are ever audible.
const remotes = new Map();

function makeRemote() {
  const src = ctx.createBufferSource(); src.buffer = NOISE; src.loop = true;
  const lp = ctx.createBiquadFilter(); lp.type = 'lowpass'; lp.frequency.value = 300;
  const pulse = ctx.createOscillator(); pulse.type = 'sine'; pulse.frequency.value = 4;
  const pAmt = ctx.createGain(); pAmt.gain.value = 0.4;
  const ch = ctx.createGain(); ch.gain.value = 0.6;
  const pan = ctx.createStereoPanner();
  const gain = ctx.createGain(); gain.gain.value = 0;
  pulse.connect(pAmt); pAmt.connect(ch.gain);
  src.connect(lp); lp.connect(ch); ch.connect(pan); pan.connect(gain); gain.connect(ambBus);
  src.start(); pulse.start();
  return { src, lp, pulse, pan, gain, nodes: [src, lp, pulse, pAmt, ch, pan, gain], d: 999, g: 0 };
}

export function remoteRotor(id, x, z, rot = 0) {
  if (!ctx) return;
  let r = remotes.get(id);
  const fresh = !r;
  if (!r) { r = makeRemote(); remotes.set(id, r); }
  const dx = x - lx, dz = z - lz;
  r.d = Math.hypot(dx, dz);
  if (fresh) rankRemotes();
  const rel = Math.atan2(dx, dz) - lyaw;
  const t = ctx.currentTime;
  const s = clamp(rot, 0, 1.2);
  r.want = s;
  r.pan.pan.value = clamp(-Math.sin(rel) * 0.7, -1, 1);   // same sign convention as spatial()
  r.lp.frequency.value = 120 + s * 260;
  r.pulse.frequency.value = 0.8 + s * (BLADE_HZ_MAX - 0.8);
  const g = r.g * s * 0.18;
  if (Math.abs(g - (r.lastG || 0)) > 0.003) { r.lastG = g; r.gain.gain.setTargetAtTime(g, t, 0.15); }
}

function rankRemotes() {
  if (remotes.size === 0) return;
  const list = [...remotes.values()].sort((a, b) => a.d - b.d);
  for (let i = 0; i < list.length; i++) {
    list[i].g = (i < 4 && list[i].d < 160) ? 1 / (1 + list[i].d / 45) : 0;
  }
}

export function dropRemoteRotor(id) {
  const r = remotes.get(id);
  if (!r) return;
  remotes.delete(id);
  try { r.src.stop(); r.pulse.stop(); } catch (e) { /* already stopped */ }
  for (const n of r.nodes) { try { n.disconnect(); } catch (e) { /* gone */ } }
}
